import type { QualityGateResult } from './api/types';

/**
 * The on-device quality gate. Three cheap, deterministic checks against raw
 * pixel data, no model call: resolution, exposure, and sharpness.
 *
 * ADVISORY ONLY. Whether a failing result blocks submission is open
 * decision B1 (architecture.md section 13, reconciliation register R1) and
 * it lives server-side. This module's result is always attached to
 * `CaptureSubmission.clientQuality` and the capture is always submitted
 * regardless of `passed`. No caller in this app may use this result to
 * disable the submit action; a UI may only use it to show the operator a
 * hint before they submit.
 */

const MIN_PIXELS = 480 * 640; // below this, downstream OCR degrades sharply
const IDEAL_MEAN_LUMINANCE = 128;
const LUMINANCE_TOLERANCE = 70; // 0-255 scale; wide, this is a coarse pre-check
const MIN_SHARPNESS_VARIANCE = 8; // empirical floor for "not obviously blurred"

export interface PixelSample {
  width: number;
  height: number;
  /** RGBA, as from `ImageData.data`. */
  data: Uint8ClampedArray;
}

export function runQualityGate(sample: PixelSample): QualityGateResult {
  const resolution = checkResolution(sample);
  const exposure = checkExposure(sample);
  const sharpness = checkSharpness(sample);

  const checks = [resolution, exposure, sharpness];
  return {
    passed: checks.every((c) => c.passed),
    checks,
  };
}

function checkResolution(sample: PixelSample): { name: string; passed: boolean; score: number } {
  const pixels = sample.width * sample.height;
  return {
    name: 'resolution',
    passed: pixels >= MIN_PIXELS,
    score: clamp01(pixels / MIN_PIXELS),
  };
}

function checkExposure(sample: PixelSample): { name: string; passed: boolean; score: number } {
  const meanLuminance = meanLuminanceOf(sample);
  const distance = Math.abs(meanLuminance - IDEAL_MEAN_LUMINANCE);
  return {
    name: 'exposure',
    passed: distance <= LUMINANCE_TOLERANCE,
    score: clamp01(1 - distance / 255),
  };
}

function checkSharpness(sample: PixelSample): { name: string; passed: boolean; score: number } {
  const variance = luminanceGradientVarianceOf(sample);
  return {
    name: 'sharpness',
    passed: variance >= MIN_SHARPNESS_VARIANCE,
    score: clamp01(variance / (MIN_SHARPNESS_VARIANCE * 4)),
  };
}

function meanLuminanceOf(sample: PixelSample): number {
  const { data } = sample;
  let sum = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 4) {
    sum += luminanceAt(data, i);
    count += 1;
  }
  return count === 0 ? 0 : sum / count;
}

/** A cheap blur proxy: the variance of the horizontal luminance gradient.
 * A sharp image has high-frequency edges (high variance); a blurred one
 * smooths them out (low variance). Not a real Laplacian, but adequate for
 * an advisory pre-check that never blocks submission. */
function luminanceGradientVarianceOf(sample: PixelSample): number {
  const { data, width, height } = sample;
  const gradients: number[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = 1; x < width; x++) {
      const i = (y * width + x) * 4;
      const iPrev = (y * width + (x - 1)) * 4;
      gradients.push(luminanceAt(data, i) - luminanceAt(data, iPrev));
    }
  }
  if (gradients.length === 0) return 0;
  const mean = gradients.reduce((a, b) => a + b, 0) / gradients.length;
  const variance = gradients.reduce((a, b) => a + (b - mean) ** 2, 0) / gradients.length;
  return variance;
}

function luminanceAt(data: Uint8ClampedArray, offset: number): number {
  const r = data[offset];
  const g = data[offset + 1];
  const b = data[offset + 2];
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Reads pixel data from a decoded image so `runQualityGate` can run
 * without every caller re-implementing canvas plumbing. Browser-only:
 * relies on `OffscreenCanvas`/`document.createElement('canvas')`. */
export async function sampleImage(bitmap: ImageBitmap): Promise<PixelSample> {
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(bitmap.width, bitmap.height)
      : Object.assign(document.createElement('canvas'), { width: bitmap.width, height: bitmap.height });
  const ctx = canvas.getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) {
    throw new Error('2D canvas context unavailable for quality-gate sampling');
  }
  ctx.drawImage(bitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  return { width: bitmap.width, height: bitmap.height, data: imageData.data };
}
